/*!
 * scoreTime.js — the one mapping between a place in the score and a time
 * © 2025 Rob Canning — GPLv3
 *
 * A score is a strip `scoreWidth` world units wide that the playhead crosses
 * in `duration` ms. So a world x IS a score time, and the conversion is a
 * proportion:
 *
 *     scoreMs = worldX / scoreWidth * duration
 *
 * It matters that this is stated in world units, because world units are the
 * only description of the score that every client agrees on. Screen pixels
 * are not: the same bar sits at a different rect.left on a laptop and a
 * phone, under a different fit scale, at a different scroll offset. Anything
 * that asks "when does this element happen?" in order to line up with ANOTHER
 * MACHINE has to ask it here — metro() derived its beat grid from a screen
 * measurement and every client got a different answer, which is exactly what
 * being out of sync sounds like.
 *
 * See worldCoords.js for getting a trustworthy world x out of an element.
 */

/** Score time (ms) at a world x, or null when the score has no extent yet. */
export function scoreTimeAtWorldX(worldX, scoreWidth, duration) {
  if (!Number.isFinite(worldX) || !(scoreWidth > 0) || !(duration > 0)) return null;
  return (worldX / scoreWidth) * duration;
}

/** World x the playhead has reached at a score time, or null. */
export function worldXAtScoreTime(scoreMs, scoreWidth, duration) {
  if (!Number.isFinite(scoreMs) || !(scoreWidth > 0) || !(duration > 0)) return null;
  return (scoreMs / duration) * scoreWidth;
}

/**
 * How far world x is from the score's start. A reversed score
 * (window.scrollReversed: right to left, bottom up) starts at scoreWidth and
 * runs down to 0, so there it is scoreWidth - x. Its own inverse: the same
 * call turns a distance from the start back into a world x.
 */
export function fromScoreStart(x, scoreWidth, reversed = false) {
  return reversed ? scoreWidth - x : x;
}

/**
 * The score time (ms) of the playhead at world x, counted from the score's
 * start (fromScoreStart). null when the score has no extent yet.
 */
export function playheadScoreTime(playheadX, scoreWidth, duration, reversed = false) {
  return scoreTimeAtWorldX(fromScoreStart(playheadX, scoreWidth, reversed), scoreWidth, duration);
}

/** The playhead's world x at a score time: playheadScoreTime the other way. */
export function playheadXAtScoreTime(scoreMs, scoreWidth, duration, reversed = false) {
  const x = worldXAtScoreTime(scoreMs, scoreWidth, duration);
  return x == null ? x : fromScoreStart(x, scoreWidth, reversed);
}

/** window.elapsedTime for where the playhead is now; as it was when the score has no extent. */
export function elapsedAtPlayhead(w = window) {
  return playheadScoreTime(w.playheadX, w.scoreWidth, w.duration, !!w.scrollReversed) ?? w.elapsedTime;
}

/**
 * Has the playhead already crossed a cue sitting at `worldX`?
 *
 * Both arguments are world x. `reversed` is window.scrollReversed: the score
 * runs the other way, so the playhead counts down and "past" flips with it.
 * An unknown position answers no — the caller then treats the cue as still
 * ahead, which is the safe direction (it re-arms rather than stays stale).
 */
export function playheadIsPast(worldX, playheadX, reversed = false) {
  if (!Number.isFinite(worldX) || !Number.isFinite(playheadX)) return false;
  return reversed ? playheadX <= worldX : playheadX >= worldX;
}

/**
 * The strip a score SVG of `width` x `height` makes: which way it scrolls
 * and how long it is along that way (scoreWidth). A portrait SVG scrolls
 * vertically; the Scroll Direction preference (`prefDir`, not "auto")
 * chooses the axis and the direction itself; otherwise the SVG's
 * data-scroll-dir (`svgDir`) can only reverse it. Reversed scores start the
 * playhead at `length`.
 */
export function scrollAxis(width, height, svgDir = "", prefDir = "auto") {
  const pref = prefDir && prefDir !== "auto" ? prefDir : null;
  const vertical = pref ? (pref === "ttb" || pref === "btu") : !!(height && width && height > width);
  const dir = pref || svgDir || "";
  return {
    vertical,
    reversed: dir === "rtl" || dir === "btu",
    length: vertical ? (height || 30000) : (width || 40960),
  };
}
